import { Button } from '../ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';

export const REJECTION_REASON_CATEGORIES = [
  'Publisher & Listing Requirements',
  'Branding & Impersonation',
  'Package & Release Eligibility',
  'Installability & Compatibility',
  'Permissions & Data Handling',
  'Core App Functionality',
  'Wallet & Transaction Safety',
  'Security & Deceptive Behavior',
] as const;

export interface RejectionReasonFormState {
  code: string;
  label: string;
  description: string;
  category: string;
}

export const emptyRejectionReasonForm: RejectionReasonFormState = {
  code: '',
  label: '',
  description: '',
  category: '',
};

export const REJECTION_REASON_CODE_PATTERN = /^[A-Z]{2,5}-\d{3}$/;

const fieldClassName =
  'w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2 text-sm text-white transition-colors hover:border-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 disabled:cursor-not-allowed disabled:opacity-50';

const invalidFieldClass =
  'border-red-500/70 hover:border-red-500 focus:border-red-500 focus:ring-red-500';

interface RejectionReasonFormProps {
  form: RejectionReasonFormState;
  isEditing: boolean;
  isSaving: boolean;
  onFieldChange: (field: keyof RejectionReasonFormState, value: string) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export function RejectionReasonForm({
  form,
  isEditing,
  isSaving,
  onFieldChange,
  onSubmit,
  onCancel,
}: RejectionReasonFormProps) {
  const trimmedCode = form.code.trim();
  const isCodeInvalidOrMissing =
    !isEditing && !REJECTION_REASON_CODE_PATTERN.test(trimmedCode);
  const isCodeFormatInvalid = isCodeInvalidOrMissing && trimmedCode.length > 0;

  const isSubmitDisabled =
    isSaving ||
    !form.label.trim() ||
    !form.description.trim() ||
    !form.category.trim() ||
    isCodeInvalidOrMissing;

  const missingFields = [
    isCodeInvalidOrMissing ? 'code (e.g. FUN-008)' : null,
    !form.label.trim() ? 'label' : null,
    !form.description.trim() ? 'description' : null,
    !form.category.trim() ? 'category' : null,
  ].filter((field): field is string => field !== null);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isSaving) {
          onCancel();
        }
      }}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit rejection reason' : 'Add rejection reason'}
          </DialogTitle>
          <DialogDescription>
            Reviewers select these when rejecting a submission.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label
                htmlFor="rejection-reason-code"
                className="text-sm font-medium text-white"
              >
                Code
              </label>
              <input
                id="rejection-reason-code"
                aria-label="Reason Code"
                value={form.code}
                disabled={isSaving || isEditing}
                placeholder="e.g. FUN-008"
                aria-invalid={isCodeInvalidOrMissing}
                onChange={(event) =>
                  onFieldChange('code', event.target.value.toUpperCase())
                }
                className={`${fieldClassName} ${
                  isCodeInvalidOrMissing ? invalidFieldClass : ''
                }`}
              />
              {isCodeFormatInvalid && (
                <span className="block text-xs text-red-400">
                  Use 2–5 uppercase letters, a dash, then 3 digits (e.g.
                  FUN-008).
                </span>
              )}
            </div>
            <div className="space-y-2">
              <label
                htmlFor="rejection-reason-label"
                className="text-sm font-medium text-white"
              >
                Label
              </label>
              <input
                id="rejection-reason-label"
                aria-label="Reason Label"
                value={form.label}
                disabled={isSaving}
                maxLength={120}
                placeholder="Short reviewer-facing label"
                aria-invalid={!form.label.trim()}
                onChange={(event) => onFieldChange('label', event.target.value)}
                className={`${fieldClassName} ${
                  !form.label.trim() ? invalidFieldClass : ''
                }`}
              />
            </div>
          </div>

          <div className="space-y-2">
            <label
              htmlFor="rejection-reason-description"
              className="text-sm font-medium text-white"
            >
              Description
            </label>
            <Textarea
              id="rejection-reason-description"
              aria-label="Reason Description"
              value={form.description}
              disabled={isSaving}
              rows={3}
              maxLength={2000}
              placeholder="The requirement this reason enforces"
              aria-invalid={!form.description.trim()}
              onChange={(event) =>
                onFieldChange('description', event.target.value)
              }
              className={`border-neutral-700 bg-neutral-950 text-white placeholder:text-neutral-500 ${
                !form.description.trim() ? invalidFieldClass : ''
              }`}
            />
          </div>

          <div className="space-y-2">
            <label
              htmlFor="rejection-reason-category"
              className="text-sm font-medium text-white"
            >
              Category
            </label>
            <select
              id="rejection-reason-category"
              aria-label="Reason Category"
              value={form.category}
              disabled={isSaving}
              aria-invalid={!form.category.trim()}
              onChange={(event) =>
                onFieldChange('category', event.target.value)
              }
              className={`${fieldClassName} ${
                !form.category.trim() ? invalidFieldClass : ''
              }`}
              style={{ colorScheme: 'dark' }}
            >
              <option value="">Select a category…</option>
              {REJECTION_REASON_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {!isSaving && isSubmitDisabled && missingFields.length > 0 && (
            <p className="text-xs text-neutral-500">
              Required to save: {missingFields.join(', ')}
            </p>
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            className="bg-teal-600 text-white hover:bg-teal-500 disabled:bg-neutral-800 disabled:text-neutral-500"
          >
            {isSaving
              ? 'Saving…'
              : isEditing
                ? 'Save changes'
                : 'Create reason'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
